import { useEffect } from "react";
import { businessConfig } from "@/config/business.config";

export interface SeoOptions {
  title: string;
  description: string;
  path: string;
  /** Optional JSON-LD structured data object(s) to inject for this page. */
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * Build the per-page <head> tags as an HTML string. Used at prerender time
 * (see prerender.mjs / entry-server) so each static page ships with its own
 * title, description, canonical, Open Graph/Twitter tags and JSON-LD in the
 * raw HTML — the same values useSeo() applies on the client at runtime.
 */
export function renderHeadToString(o: SeoOptions): string {
  const fullTitle = `${o.title} | ${businessConfig.seo.defaultTitleSuffix}`;
  const img = `${businessConfig.seo.baseUrl}${businessConfig.seo.ogImage}`;
  const url = `${businessConfig.seo.baseUrl}${o.path}`;
  const tags = [
    `<title>${escapeHtml(fullTitle)}</title>`,
    `<meta name="description" content="${escapeHtml(o.description)}" />`,
    `<link rel="canonical" href="${escapeHtml(url)}" />`,
    `<meta property="og:site_name" content="${escapeHtml(businessConfig.seo.siteName)}" />`,
    `<meta property="og:locale" content="en_AU" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:title" content="${escapeHtml(fullTitle)}" />`,
    `<meta property="og:description" content="${escapeHtml(o.description)}" />`,
    `<meta property="og:url" content="${escapeHtml(url)}" />`,
    `<meta property="og:image" content="${escapeHtml(img)}" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escapeHtml(fullTitle)}" />`,
    `<meta name="twitter:description" content="${escapeHtml(o.description)}" />`,
    `<meta name="twitter:image" content="${escapeHtml(img)}" />`,
  ];
  if (o.structuredData) {
    const arr = Array.isArray(o.structuredData) ? o.structuredData : [o.structuredData];
    for (const d of arr) {
      tags.push(`<script type="application/ld+json">${JSON.stringify(d)}</script>`);
    }
  }
  return tags.join("\n    ");
}

function setMetaTag(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkTag(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Sets per-page title, meta description, canonical URL, Open Graph tags,
 * and (optionally) JSON-LD structured data. No external head-management
 * library is used to keep the dependency footprint small, this hook
 * covers everything the brief requires.
 */
export function useSeo({ title, description, path, structuredData }: SeoOptions) {
  useEffect(() => {
    const fullTitle = `${title} | ${businessConfig.seo.defaultTitleSuffix}`;
    document.title = fullTitle;

    const imageUrl = `${businessConfig.seo.baseUrl}${businessConfig.seo.ogImage}`;

    setMetaTag("name", "description", description);
    setMetaTag("property", "og:site_name", businessConfig.seo.siteName);
    setMetaTag("property", "og:locale", "en_AU");
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:type", "website");
    setMetaTag("property", "og:image", imageUrl);
    setMetaTag("property", "og:image:width", "1200");
    setMetaTag("property", "og:image:height", "630");
    setMetaTag("property", "og:url", `${businessConfig.seo.baseUrl}${path}`);
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", fullTitle);
    setMetaTag("name", "twitter:description", description);
    setMetaTag("name", "twitter:image", imageUrl);

    setLinkTag("canonical", `${businessConfig.seo.baseUrl}${path}`);

    let script: HTMLScriptElement | null = null;
    if (structuredData) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify(structuredData);
      script.dataset.seoInjected = "true";
      document.head.appendChild(script);
    }

    return () => {
      if (script) {
        document.head.removeChild(script);
      }
    };
  }, [title, description, path, structuredData]);
}
